// -----------------------------------------------------------------------------
// HomeHero.jsx — Traffic-aware homepage hero renderer.
//
// Behaves as follows on the homepage:
//   1. On first render (SSR / prerender / no window), returns the flagship
//      markup exactly so JS-less crawlers and prerendered HTML always see the
//      protected image.
//   2. On client hydration, reads window.__lscHero (populated by the inline
//      script in public/index.html BEFORE React booted) to determine the
//      already-resolved variant. Also persists to sessionStorage so
//      navigation and refresh keep the selection stable.
//   3. Emits one hero_variant_view analytics event per session per landing.
//   4. Never rotates. Never randomizes. Never changes on a timer.
//   5. Layout / dimensions / copy / CTAs are ALL preserved regardless of
//      which variant is selected — only the image src, srcset, and alt
//      change.
// -----------------------------------------------------------------------------
import { useEffect, useMemo, useRef, useState } from "react";
import {
  HERO_VARIANTS,
  DEFAULT_VARIANT,
  HERO_KILL_SWITCH,
  selectHeroVariant,
  ROUTE_INTENT_MAP,
  SELECTION_SOURCES,
} from "@/lib/hero";

const heroWarn = (label, err) => { if (typeof console !== "undefined") console.warn("[hero] " + label + ":", err); };

const SESSION_KEY = "lsc.hero.variant";
const SESSION_SOURCE_KEY = "lsc.hero.source";
const ANALYTICS_FIRED_KEY = "lsc.hero.analyticsFired";
const EXPERIMENT_VERSION = "traffic-aware-v1";

function safeSession() {
  // sessionStorage may throw in privacy modes — treat all errors as absent.
  try { return typeof window !== "undefined" ? window.sessionStorage : null; }
  catch { return null; }
}

function resolveOnClient() {
  if (typeof window === "undefined") return { variant: DEFAULT_VARIANT, source: SELECTION_SOURCES.DEFAULT_FALLBACK };

  // Preferred path — the inline bootstrap script wrote the resolved decision
  // to window.__lscHero before React booted. Trust it.
  const preboot = window.__lscHero;
  if (preboot && HERO_VARIANTS[preboot.variant]) return preboot;

  // Fallback path — bootstrap didn't run (e.g. dev-tool hot reload). Compute
  // the same decision from live signals so the component still works.
  const url = new URL(window.location.href);
  const params = url.searchParams;
  const ss = safeSession();
  let sessionVariant = null;
  try { sessionVariant = ss ? ss.getItem(SESSION_KEY) : null; } catch (err) { heroWarn("catch_1", err); }
  let sameSiteReferrerPath = null;
  try {
    const ref = document.referrer;
    if (ref) {
      const refUrl = new URL(ref);
      if (refUrl.host === window.location.host) sameSiteReferrerPath = refUrl.pathname;
    }
  } catch (err) { heroWarn("catch_2", err); }
  return selectHeroVariant({
    killSwitchForceFlagship: HERO_KILL_SWITCH.forceFlagship,
    lscIntentParam: params.get("lsc_intent"),
    sessionVariant,
    sameSiteReferrerPath,
    utmCampaign: params.get("utm_campaign"),
    utmContent: params.get("utm_content"),
  });
}

function emitAnalyticsOnce({ variant, source, landingPath, campaignId }) {
  const ss = safeSession();
  try {
    if (ss && ss.getItem(ANALYTICS_FIRED_KEY) === variant) return;
    if (ss) ss.setItem(ANALYTICS_FIRED_KEY, variant);
  } catch (err) { heroWarn("catch_3", err); }
  const payload = {
    variant,
    selection_source: source,
    landing_path: landingPath,
    experiment_version: EXPERIMENT_VERSION,
    ...(campaignId ? { campaign_id: campaignId } : {}),
  };
  // Analytics adapter contract — the existing analytics layer (if any)
  // reads `window.dataLayer` (GA-style) or listens on document for a
  // CustomEvent. We fire both so whichever is wired up will pick it up.
  try {
    if (typeof window !== "undefined") {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ event: "hero_variant_view", ...payload });
      document.dispatchEvent(new CustomEvent("hero_variant_view", { detail: payload }));
    }
  } catch (err) { heroWarn("catch_4", err); }
  // Preserve for support-side debugging without exposing on the DOM.
  try { if (typeof window !== "undefined") window.__lscHeroAnalytics = payload; } catch (err) { heroWarn("catch_5", err); }
}

export default function HomeHero({
  children,
  className = "",
  overlayScrim,
  minHeightClass = "min-h-[620px] md:min-h-[720px]",
}) {
  // Deterministic first render — flagship, always. Prevents hydration
  // mismatch and gives JS-less crawlers the correct fallback.
  const [decision, setDecision] = useState({ variant: DEFAULT_VARIANT, source: SELECTION_SOURCES.DEFAULT_FALLBACK });
  const analyticsFiredRef = useRef(false);

  useEffect(() => {
    const resolved = resolveOnClient();
    setDecision(resolved);

    // Persist to session so the choice survives same-session navigation.
    const ss = safeSession();
    try {
      if (ss) {
        ss.setItem(SESSION_KEY, resolved.variant);
        ss.setItem(SESSION_SOURCE_KEY, resolved.source);
      }
    } catch (err) { heroWarn("catch_6", err); }

    // Fire the analytics event exactly once per session-per-variant.
    if (!analyticsFiredRef.current) {
      analyticsFiredRef.current = true;
      let campaignId = null;
      try {
        const u = new URL(window.location.href);
        campaignId = u.searchParams.get("utm_campaign") || u.searchParams.get("lsc_intent") || null;
      } catch (err) { heroWarn("catch_7", err); }
      emitAnalyticsOnce({
        variant: resolved.variant,
        source: resolved.source,
        landingPath: (typeof window !== "undefined" && window.location) ? window.location.pathname : "/",
        campaignId,
      });
    }
    // This effect intentionally runs once per mount. All referenced helpers
    // (resolveOnClient, safeSession, emitAnalyticsOnce, heroWarn, HERO_VARIANTS,
    // DEFAULT_VARIANT, SESSION_KEY, SESSION_SOURCE_KEY) are stable module-level
    // references; setDecision is a stable React setter; analyticsFiredRef is
    // a ref (also stable). Empty deps are correct.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const variant = HERO_VARIANTS[decision.variant] || HERO_VARIANTS[DEFAULT_VARIANT];
  const img = variant.image;

  // Optional dev-only variant marker — visible only when ?lsc_preview=1 is
  // present. Not indexable (no server-side rendering of the marker) and not
  // part of the accessible tree beyond a subtle badge for staff review.
  const previewMarker = useMemo(() => {
    if (typeof window === "undefined") return null;
    try {
      const u = new URL(window.location.href);
      if (u.searchParams.get("lsc_preview") !== "1") return null;
    } catch { return null; }
    return (
      <div
        className="absolute top-4 left-4 z-20 bg-navy/85 text-brass px-3 py-1.5 text-[10px] uppercase tracking-[0.24em] font-medium pointer-events-none"
        data-testid="hero-preview-marker"
        aria-hidden="true"
      >
        {variant.variant} · {decision.source}
      </div>
    );
  }, [variant.variant, decision.source]);

  return (
    <section
      className={`relative bg-mahogany overflow-hidden ${className}`}
      data-testid="hero-section"
      data-hero-variant={variant.variant}
      data-hero-source={decision.source}
    >
      {/* Background photograph — exactly one <img>, only the selected variant */}
      <div className="absolute inset-0">
        <img
          src={img.desktop}
          alt={variant.alt}
          className="w-full h-full object-cover"
          loading="eager"
          fetchPriority="high"
          decoding="async"
          width={img.dimensions.width}
          height={img.dimensions.height}
          style={{ objectPosition: img.focal.desktop }}
          data-testid="hero-image"
        />
        {/* Navy scrim on the left for headline legibility — matches original */}
        {overlayScrim !== null && (
          <div
            className="absolute inset-0"
            style={{
              background: overlayScrim ||
                "linear-gradient(90deg, rgba(8,13,27,0.78) 0%, rgba(8,13,27,0.55) 24%, rgba(8,13,27,0.10) 50%, rgba(8,13,27,0) 100%)",
            }}
          />
        )}
        {/* Top nav shadow */}
        <div className="absolute inset-x-0 top-0 h-32 bg-gradient-to-b from-navy/80 to-transparent pointer-events-none" />
      </div>

      {previewMarker}

      {/* Reserve aspect ratio and provide the copy slot */}
      <div className={`relative max-w-[1600px] mx-auto px-6 md:px-12 lg:px-16 pt-32 md:pt-40 pb-24 md:pb-32 ${minHeightClass} flex items-center`}>
        {children}
      </div>

      {/* JS-less crawler / error fallback — always the flagship. Rendered as
          a <noscript> so JS-enabled clients never see it. */}
      <noscript>
        {/* eslint-disable-next-line react/no-unknown-property */}
        <style>{`.__lsc_noscript_hero{position:absolute;inset:0;background:url(${HERO_VARIANTS[DEFAULT_VARIANT].image.desktop}) center/cover no-repeat;}`}</style>
        <div className="__lsc_noscript_hero" aria-hidden="true" />
      </noscript>
    </section>
  );
}

export { ROUTE_INTENT_MAP };
